/**
 * Space Service
 *
 * CRUD and invitation management for collaboration spaces.
 * A space is a bounded working context (personal, team, org) within a workspace.
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import type {
  CollaborationSpace,
  SpaceMembership,
  SpaceInvitation,
  MySpace,
  SpaceMembershipRole,
  SpaceType,
} from '../types/prioritization/collaborationSpaces';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fromDbSpace(row: Record<string, unknown>): CollaborationSpace {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    spaceType: row.space_type as SpaceType,
    orgUnitId: row.org_unit_id as string | undefined,
    ownerId: row.owner_id as string | undefined,
    allowGuests: row.allow_guests as boolean,
    settings: (row.settings as Record<string, unknown>) ?? {},
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function fromDbMembership(row: Record<string, unknown>): SpaceMembership {
  return {
    id: row.id as string,
    spaceId: row.space_id as string,
    userId: row.user_id as string,
    role: row.role as SpaceMembershipRole,
    status: row.status as SpaceMembership['status'],
    invitedBy: row.invited_by as string | undefined,
    invitedAt: row.invited_at ? new Date(row.invited_at as string) : undefined,
    acceptedAt: row.accepted_at ? new Date(row.accepted_at as string) : undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// ── Space reads ────────────────────────────────────────────────────────────────

export interface MySpacesResult {
  spaces: MySpace[];
  /**
   * False when the RPC itself failed (network blip, 5xx, RLS error). Callers must
   * treat this differently from an honest empty list: a failed read is retryable,
   * an empty read is a real answer. Collapsing the two is what left the space
   * switcher stuck on "Select a space…" after a single transient 503.
   */
  ok: boolean;
}

function fromDbMySpace(row: Record<string, unknown>): MySpace {
  return {
    id: row.space_id as string,
    workspaceId: row.workspace_id as string,
    name: row.space_name as string,
    spaceType: row.space_type as SpaceType,
    orgUnitId: row.org_unit_id as string | undefined,
    ownerId: row.owner_id as string | undefined,
    allowGuests: true,
    settings: {},
    memberRole: row.member_role as SpaceMembershipRole,
    memberStatus: row.member_status as SpaceMembership['status'],
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.created_at as string),
  };
}

/**
 * Returns all spaces the current user is an active member of, plus whether the
 * read actually succeeded. Prefer this over getMySpaces() anywhere the caller
 * can retry.
 */
export async function getMySpacesResult(workspaceId?: string): Promise<MySpacesResult> {
  if (!isSupabaseConfigured()) return { spaces: [], ok: false };
  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase.rpc('get_my_spaces', {
      p_workspace_id: workspaceId ?? null,
    });

    if (error) {
      console.error('[SpaceService] getMySpaces error', error);
      return { spaces: [], ok: false };
    }

    return { spaces: (data ?? []).map(fromDbMySpace), ok: true };
  } catch (err) {
    console.error('[SpaceService] getMySpaces threw', err);
    return { spaces: [], ok: false };
  }
}

/** Returns all spaces the current user is an active member of. */
export async function getMySpaces(workspaceId?: string): Promise<MySpace[]> {
  return (await getMySpacesResult(workspaceId)).spaces;
}

/** Get a single space by id. */
export async function getSpace(spaceId: string): Promise<CollaborationSpace | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('spaces')
    .select('*')
    .eq('id', spaceId)
    .single();

  if (error || !data) return null;
  return fromDbSpace(data as Record<string, unknown>);
}

/** Get all members of a space via SECURITY DEFINER RPC (avoids RLS self-join
 *  recursion that would occur on a direct space_memberships table query). */
export async function getSpaceMembers(spaceId: string): Promise<SpaceMembership[]> {
  if (!isSupabaseConfigured()) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('get_space_members_rpc', { p_space_id: spaceId });
  if (error) {
    console.error('[SpaceService] getSpaceMembers error', error);
    return [];
  }

  return (data ?? []).map(row => fromDbMembership(row as Record<string, unknown>));
}

/** Get the calling user's membership for a space, or null if not a member. */
export async function getMyMembership(spaceId: string, userId: string): Promise<SpaceMembership | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('space_memberships')
    .select('*')
    .eq('space_id', spaceId)
    .eq('user_id', userId)
    .single();

  if (error || !data) return null;
  return fromDbMembership(data as Record<string, unknown>);
}

// ── Space writes ───────────────────────────────────────────────────────────────

export interface CreateSpaceParams {
  workspaceId: string;
  name: string;
  description?: string;
  spaceType: SpaceType;
  orgUnitId?: string;
  ownerId?: string;
  allowGuests?: boolean;
}

/** Create a new space. The caller is automatically made the owner. */
export async function createSpace(params: CreateSpaceParams): Promise<CollaborationSpace | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('create_space_rpc', {
    p_workspace_id: params.workspaceId,
    p_name: params.name,
    p_space_type: params.spaceType,
    p_description: params.description ?? null,
    p_org_unit_id: params.orgUnitId ?? null,
    p_allow_guests: params.allowGuests ?? true,
  });

  if (error) {
    console.error('[SpaceService] createSpace error', error);
    return null;
  }

  const result = data as { success: boolean; space_id?: string; error?: string };
  if (!result.success) {
    console.error('[SpaceService] createSpace denied', result.error);
    return null;
  }

  return getSpace(result.space_id!);
}

/** Update space metadata. */
export async function updateSpace(
  spaceId: string,
  updates: Partial<Pick<CollaborationSpace, 'name' | 'description' | 'allowGuests' | 'settings'>>
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  const supabase = getSupabaseClient();

  const dbUpdates: Record<string, unknown> = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.allowGuests !== undefined) dbUpdates.allow_guests = updates.allowGuests;
  if (updates.settings !== undefined) dbUpdates.settings = updates.settings;

  const { error } = await supabase.from('spaces').update(dbUpdates).eq('id', spaceId);
  if (error) {
    console.error('[SpaceService] updateSpace error', error);
    return false;
  }
  return true;
}

/** Delete a space (only owner or workspace admin). Uses a SECURITY DEFINER
 *  RPC so that space membership ownership is checked server-side and the
 *  direct-table DELETE RLS (which only looks at spaces.owner_id) is bypassed
 *  for team/org spaces where owner_id is NULL. */
export async function deleteSpace(spaceId: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('delete_space_rpc', { p_space_id: spaceId });
  if (error) {
    console.error('[SpaceService] deleteSpace error', error);
    return false;
  }
  const result = data as { success: boolean; error?: string } | null;
  if (!result?.success) {
    console.error('[SpaceService] deleteSpace denied –', result?.error);
    return false;
  }
  return true;
}

// ── Membership writes ─────────────────────────────────────────────────────────

export interface MembershipMutationResult {
  success: boolean;
  blocked?: { reasonCodes: string[]; message: string };
  error?: string;
}

function interpretRpcResult(data: unknown): MembershipMutationResult {
  const r = data as { success?: boolean; error?: string; reasons?: Array<{ code: string }> } | null;
  if (r?.success) return { success: true };
  if (r?.reasons && r.reasons.length > 0) {
    return {
      success: false,
      blocked: {
        reasonCodes: r.reasons.map(x => x.code),
        message: r.error ?? `Blocked by policy: ${r.reasons.map(x => x.code).join(', ')}`,
      },
    };
  }
  return { success: false, error: r?.error ?? 'Unknown error' };
}

/** Add a user to a space with the specified role. Routed through SECURITY DEFINER RPC for policy enforcement. */
export async function addSpaceMember(
  spaceId: string,
  userId: string,
  role: SpaceMembershipRole,
  opts?: { approvalGranted?: boolean }
): Promise<MembershipMutationResult> {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('add_space_member_rpc', {
    p_space_id: spaceId,
    p_user_id: userId,
    p_role: role,
    p_approval_granted: opts?.approvalGranted ?? false,
  });

  if (error) {
    console.error('[SpaceService] addSpaceMember error', error);
    return { success: false, error: error.message };
  }
  return interpretRpcResult(data);
}

/** Update a member's role in a space. Routed through SECURITY DEFINER RPC for policy enforcement. */
export async function updateSpaceMemberRole(
  spaceId: string,
  userId: string,
  role: SpaceMembershipRole,
  opts?: { approvalGranted?: boolean }
): Promise<MembershipMutationResult> {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('change_space_member_role_rpc', {
    p_space_id: spaceId,
    p_user_id: userId,
    p_new_role: role,
    p_approval_granted: opts?.approvalGranted ?? false,
  });

  if (error) {
    console.error('[SpaceService] updateSpaceMemberRole error', error);
    return { success: false, error: error.message };
  }
  return interpretRpcResult(data);
}

/** Remove a user from a space. Routed through SECURITY DEFINER RPC for policy enforcement. */
export async function removeSpaceMember(
  spaceId: string,
  userId: string,
  opts?: { approvalGranted?: boolean }
): Promise<MembershipMutationResult> {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('remove_space_member_rpc', {
    p_space_id: spaceId,
    p_user_id: userId,
    p_approval_granted: opts?.approvalGranted ?? false,
  });

  if (error) {
    console.error('[SpaceService] removeSpaceMember error', error);
    return { success: false, error: error.message };
  }
  return interpretRpcResult(data);
}

// ── Invitations ────────────────────────────────────────────────────────────────

export interface CreateSpaceInvitationParams {
  spaceId: string;
  email?: string;
  role?: Exclude<SpaceMembershipRole, 'owner'>;
  expiresInDays?: number;
  /** Pass true if approval has been obtained via a separate flow. */
  approvalGranted?: boolean;
}

export interface CreateInvitationResult {
  invitation?: SpaceInvitation;
  blocked?: { reasonCodes: string[]; message: string };
  error?: string;
}

/**
 * Create a shareable invite link for a space.
 * Routed through SECURITY DEFINER RPC so policy enforcement can't be bypassed
 * by clients hitting the table directly.
 */
export async function createSpaceInvitation(
  params: CreateSpaceInvitationParams
): Promise<CreateInvitationResult> {
  if (!isSupabaseConfigured()) return { error: 'Supabase not configured' };
  const supabase = getSupabaseClient();

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Not authenticated' };

  const { data, error } = await supabase.rpc('create_space_invitation_rpc', {
    p_space_id: params.spaceId,
    p_email: params.email ?? null,
    p_role: params.role ?? 'member',
    p_expires_in_days: params.expiresInDays ?? 7,
    p_approval_granted: params.approvalGranted ?? false,
  });

  if (error) {
    console.error('[SpaceService] createSpaceInvitation error', error);
    return { error: error.message };
  }

  const result = data as {
    success: boolean;
    error?: string;
    reasons?: Array<{ code: string; policy_name?: string }>;
    id?: string;
    token?: string;
    expires_at?: string;
    role?: string;
  };

  if (!result.success) {
    if (result.reasons && result.reasons.length > 0) {
      return {
        blocked: {
          reasonCodes: result.reasons.map(r => r.code),
          message: result.error ?? `Blocked by space policy: ${result.reasons.map(r => r.code).join(', ')}`,
        },
      };
    }
    return { error: result.error ?? 'Failed to create invitation' };
  }

  return {
    invitation: {
      id: result.id!,
      spaceId: params.spaceId,
      token: result.token!,
      email: params.email,
      role: (result.role as Exclude<SpaceMembershipRole, 'owner'>) ?? params.role ?? 'member',
      invitedBy: user.id,
      expiresAt: result.expires_at ? new Date(result.expires_at) : new Date(),
      createdAt: new Date(),
    },
  };
}

/** Accept a space invitation by token. */
export async function acceptSpaceInvitation(token: string): Promise<{
  success: boolean;
  spaceId?: string;
  spaceName?: string;
  error?: string;
}> {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('accept_space_invitation', { p_token: token });

  if (error) {
    console.error('[SpaceService] acceptSpaceInvitation error', error);
    return { success: false, error: error.message };
  }

  return data as { success: boolean; spaceId?: string; spaceName?: string; error?: string };
}

/** Get pending invitations for a space. */
export async function getSpaceInvitations(spaceId: string): Promise<SpaceInvitation[]> {
  if (!isSupabaseConfigured()) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('space_invitations')
    .select('*')
    .eq('space_id', spaceId)
    .is('used_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[SpaceService] getSpaceInvitations error', error);
    return [];
  }

  return (data ?? []).map(row => ({
    id: row.id as string,
    spaceId: row.space_id as string,
    token: row.token as string,
    email: row.email as string | undefined,
    role: row.role as SpaceInvitation['role'],
    invitedBy: row.invited_by as string,
    expiresAt: new Date(row.expires_at as string),
    usedAt: row.used_at ? new Date(row.used_at as string) : undefined,
    usedBy: row.used_by as string | undefined,
    createdAt: new Date(row.created_at as string),
  }));
}

// ── Bootstrap helpers ─────────────────────────────────────────────────────────

/** Ensure the calling user has a personal space in the given workspace. Idempotent. */
export async function ensurePersonalSpace(workspaceId: string): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('ensure_personal_space', {
    p_workspace_id: workspaceId,
  });

  if (error) {
    console.error('[SpaceService] ensurePersonalSpace error', error);
    return null;
  }

  const result = data as { success: boolean; space_id?: string; error?: string };
  if (!result.success) {
    console.warn('[SpaceService] ensurePersonalSpace denied:', result.error ?? 'unknown');
    return null;
  }
  return result.space_id ?? null;
}

/** Ensure the org-wide space for a workspace exists. Idempotent. Requires workspace admin. */
export async function ensureOrgSpace(workspaceId: string): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('ensure_org_space', {
    p_workspace_id: workspaceId,
  });

  if (error) {
    console.error('[SpaceService] ensureOrgSpace error', error);
    return null;
  }

  const result = data as { success: boolean; space_id?: string; error?: string };
  if (!result.success) {
    console.warn('[SpaceService] ensureOrgSpace denied:', result.error ?? 'unknown');
    return null;
  }
  return result.space_id ?? null;
}

/** Ensure a team space for an org_unit exists. Idempotent. */
export async function ensureTeamSpace(workspaceId: string, orgUnitId: string): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('ensure_team_space', {
    p_workspace_id: workspaceId,
    p_org_unit_id: orgUnitId,
  });

  if (error) {
    console.error('[SpaceService] ensureTeamSpace error', error);
    return null;
  }

  const result = data as { success: boolean; space_id?: string; error?: string };
  return result.success ? (result.space_id ?? null) : null;
}
